// imageCache.js

/**
 * Stores successfully resolved signed URLs.
 * Key: original image src, Value: signed URL string
 */
const resolvedUrlCache = new Map();

/**
 * Stores promises for in-flight image fetch requests to prevent "cache stampede".
 * Key: original image src, Value: Promise of the fetch operation
 */
const inflightRequests = new Map();

/**
 * Fetches a signed URL for a given source.
 * 1. Checks for a resolved URL in `resolvedUrlCache`.
 * 2. If not found, checks for an in-flight request in `inflightRequests`.
 * 3. If neither, initiates a new fetch, stores its promise in `inflightRequests`.
 * Upon successful fetch, stores the URL in `resolvedUrlCache` and removes from `inflightRequests`.
 * Upon failure, removes from `inflightRequests` to allow retries.
 *
 * @param {string} src The original source URL of the image.
 * @param {string} token The authorization token.
 * @returns {Promise<string>} A promise that resolves to the signed URL.
 */
export function getOrFetchSignedUrl(src, token) {
  // 1. Check resolved URL cache first
  if (resolvedUrlCache.has(src)) {
    return Promise.resolve(resolvedUrlCache.get(src));
  }

  // 2. Check for an in-flight request
  if (inflightRequests.has(src)) {
    return inflightRequests.get(src);
  }

  // 3. No resolved URL and no in-flight request, so initiate a new fetch
  if (!token) {
    return Promise.reject(
      new Error('Auth token is required to fetch signed URL.'),
    );
  }

  const promise = fetch(src, {
    method: 'GET',
    headers: { Authorization: `Bearer ${token}` },
    credentials: 'include',
  })
    .then((response) => {
      if (!response.ok) {
        // Important: Still remove from inflightRequests on HTTP error
        // so future attempts can retry.
        inflightRequests.delete(src);
        throw new Error(`HTTP ${response.status} for ${src}`);
      }
      return response.json();
    })
    .then((imageResponse) => {
      if (!imageResponse || !imageResponse.url) {
        inflightRequests.delete(src); // Remove from inflight on invalid response
        throw new Error(`Invalid response structure or no URL for ${src}`);
      }

      const signedUrl = imageResponse.url;
      resolvedUrlCache.set(src, signedUrl); // Cache the successfully resolved URL
      inflightRequests.delete(src); // Remove from inflight as it's now resolved and cached
      return signedUrl;
    })
    .catch((error) => {
      // On any error in the chain, ensure it's removed from inflightRequests
      inflightRequests.delete(src);
      // Re-throw the error so the caller (AuthImage component) can handle it.
      throw error;
    });

  // Store the new promise in the in-flight cache
  inflightRequests.set(src, promise);
  return promise;
}

/**
 * Clears all cached data (both in-flight and resolved).
 * Useful for logout or major state changes.
 */
export function clearAllImageCaches() {
  resolvedUrlCache.clear();
  inflightRequests.clear();
}

/**
 * Clears cached data for a specific src.
 * @param {string} src The original source URL of the image.
 */
export function clearImageCacheForSrc(src) {
  resolvedUrlCache.delete(src);
  inflightRequests.delete(src);
}
